"use client";

import { useEffect, useId, type ReactNode } from "react";
import { CloseIcon } from "./lifestyleIcons";

interface BottomSheetProps {
  isOpen: boolean;
  /** Centred heading, and the sheet's accessible name. */
  title: string;
  /** Top-right dismiss affordance, for sheets with no footer CTA to close them. */
  showCloseButton?: boolean;
  /** Sits below the titled section, separated by the sheet's own gap. */
  footer?: ReactNode;
  onClose: () => void;
  children: ReactNode;
}

/**
 * The mobile bottom sheet shared by the lifestyle page.
 *
 * Mobile only, and the sheet closes itself if the viewport crosses into desktop
 * so the scroll lock can't outlive it.
 *
 * Callers must render this from the page: both the hero and the sticky filter
 * bar are their own stacking contexts, which cap their children's z-index and
 * would leave the sheet painting under the site header.
 */
export default function BottomSheet({
  isOpen,
  title,
  showCloseButton = false,
  footer,
  onClose,
  children,
}: BottomSheetProps) {
  const titleId = useId();

  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);

    const desktop = window.matchMedia("(min-width: 1024px)");
    const onBreakpointChange = () => {
      if (desktop.matches) onClose();
    };
    desktop.addEventListener("change", onBreakpointChange);

    // Stop the page behind the sheet from scrolling.
    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      desktop.removeEventListener("change", onBreakpointChange);
      document.body.style.overflow = overflow;
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    // Matches the z-level of FilterOptionsModal so the site header at z-[1003]
    // and its popovers stay behind the sheet.
    <div className="lg:hidden fixed inset-0 z-[9999] flex items-end font-jost">
      <button
        type="button"
        onClick={onClose}
        aria-label={`Close ${title}`}
        className="absolute inset-0 bg-black/60 cursor-default"
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative flex flex-col items-center w-full max-h-full overflow-y-auto px-4 py-6 gap-6 bg-white rounded-t-2xl backdrop-blur-[6px]">
        {showCloseButton && (
          <button
            type="button"
            onClick={onClose}
            aria-label={`Close ${title}`}
            className="flex items-center justify-center absolute top-3 right-3 size-6 text-gray-5 hover:text-dark-1 transition-colors">
            <CloseIcon className="size-6" />
          </button>
        )}

        <div className="flex flex-col w-full gap-6">
          <h2 id={titleId} className="text-xl font-medium leading-6 text-dark-1 text-center !mb-0">
            {title}
          </h2>

          {children}
        </div>

        {footer}
      </div>
    </div>
  );
}
